import React, { useState } from 'react';
import {
  VStack, SimpleGrid, FormControl, FormLabel, Input, Textarea,
  Button, HStack, Box, Text, useToast,
} from '@chakra-ui/react';
import {
  ImmConstancia, crearConstancia, actualizarConstancia, descargarPdfConstancia,
} from '../../../api/immApi';
import {
  SectionTitle, Field, ExpedienteSelect, IMM_COLOR, IMM_COLOR_HOVER, fmt,
} from '../_shared';

type Modo = 'new' | 'edit' | 'view';

interface Props {
  modo: Modo;
  data?: ImmConstancia;
  onGuardado?: (c: ImmConstancia) => void;
  onCancelar?: () => void;
}

const hoy = () => new Date().toISOString().split('T')[0];

const FormularioConstancia: React.FC<Props> = ({ modo, data, onGuardado, onCancelar }) => {
  const toast = useToast();
  const [guardando, setGuardando] = useState(false);
  const [descargando, setDescargando] = useState(false);
  const [expedienteId, setExpedienteId] = useState<number | undefined>(data?.expedienteId);

  const handleDescargarPdf = async () => {
    if (!data) return;
    setDescargando(true);
    try {
      await descargarPdfConstancia(data.id);
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo abrir la constancia.', status: 'error', duration: 4000 });
    } finally {
      setDescargando(false);
    }
  };

  const [f, setF] = useState<Partial<ImmConstancia>>({
    fecha: data?.fecha ? data.fecha.split('T')[0] : hoy(),
    tipoDocumento: data?.tipoDocumento || '',
    motivo: data?.motivo || '',
    textoInstitucional: data?.textoInstitucional || '',
    servidorPublico: data?.servidorPublico || '',
    cargo: data?.cargo || '',
    dependenciaEmisora: data?.dependenciaEmisora || 'Instancia Municipal de la Mujer',
  });

  const set = (campo: keyof ImmConstancia, valor: any) => setF((prev) => ({ ...prev, [campo]: valor }));

  const handleGuardar = async () => {
    if (!expedienteId) {
      toast({ title: 'Selecciona un expediente', status: 'warning', duration: 3000 });
      return;
    }
    setGuardando(true);
    try {
      const payload: Partial<ImmConstancia> = { ...f, expedienteId };
      const res = modo === 'edit' && data
        ? await actualizarConstancia(data.id, payload)
        : await crearConstancia(payload);
      toast({ title: modo === 'edit' ? 'Constancia actualizada' : 'Constancia creada', status: 'success', duration: 3000 });
      onGuardado?.(res);
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo guardar la constancia.', status: 'error', duration: 4000 });
    } finally {
      setGuardando(false);
    }
  };

  if (modo === 'view' && data) {
    return (
      <VStack spacing={3} align="stretch">
        <SectionTitle>Datos de la constancia</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          <Field label="Folio" value={data.folio} />
          <Field label="Token público" value={data.tokenPublico} />
          <Field label="Estatus" value={data.estatus} />
          <Field label="Expediente" value={data.expediente?.folio ?? ('#' + data.expedienteId)} />
          <Field label="Fecha" value={fmt(data.fecha)} />
          <Field label="Tipo de documento" value={data.tipoDocumento} />
          <Field label="Servidor público" value={data.servidorPublico} />
          <Field label="Cargo" value={data.cargo} />
          <Field label="Dependencia emisora" value={data.dependenciaEmisora} />
        </SimpleGrid>
        <SectionTitle>Contenido</SectionTitle>
        <SimpleGrid columns={{ base: 1 }} spacing={4}>
          <Field label="Motivo" value={data.motivo} />
          <Field label="Texto institucional" value={data.textoInstitucional} />
        </SimpleGrid>
        <SectionTitle>Documento</SectionTitle>
        <Box bg="red.50" borderRadius="md" p={3}>
          <Text fontSize="sm" color={IMM_COLOR} mb={3}>
            Descarga la constancia oficial en PDF. Incluye código QR para su validación pública.
          </Text>
          <Button size="sm" bg={IMM_COLOR} color="white" _hover={{ bg: IMM_COLOR_HOVER }}
            onClick={handleDescargarPdf} isLoading={descargando} loadingText="Generando..."
            isDisabled={data.estatus === 'Cancelada'}>
            Descargar constancia (PDF con QR)
          </Button>
          {data.estatus === 'Cancelada' && (
            <Text fontSize="xs" color="gray.500" mt={2}>La constancia está cancelada.</Text>
          )}
        </Box>
        <HStack justify="flex-end" pt={2}>
          <Button variant="outline" onClick={onCancelar}>Cerrar</Button>
        </HStack>
      </VStack>
    );
  }

  return (
    <VStack spacing={3} align="stretch">
      <SectionTitle>Datos de la constancia</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Expediente</FormLabel>
          <ExpedienteSelect value={expedienteId} onChange={setExpedienteId} isDisabled={modo !== 'new'} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Fecha</FormLabel>
          <Input type="date" value={(f.fecha as string) || ''} onChange={(e) => set('fecha', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Tipo de documento</FormLabel>
          <Input value={f.tipoDocumento || ''} onChange={(e) => set('tipoDocumento', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Servidor público</FormLabel>
          <Input value={f.servidorPublico || ''} onChange={(e) => set('servidorPublico', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Cargo</FormLabel>
          <Input value={f.cargo || ''} onChange={(e) => set('cargo', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Dependencia emisora</FormLabel>
          <Input value={f.dependenciaEmisora || ''} onChange={(e) => set('dependenciaEmisora', e.target.value)} />
        </FormControl>
      </SimpleGrid>

      <SectionTitle>Contenido</SectionTitle>
      <SimpleGrid columns={{ base: 1 }} spacing={4}>
        <FormControl>
          <FormLabel fontSize="sm">Motivo</FormLabel>
          <Input value={f.motivo || ''} onChange={(e) => set('motivo', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Texto institucional</FormLabel>
          <Textarea rows={4} value={f.textoInstitucional || ''} onChange={(e) => set('textoInstitucional', e.target.value)} />
        </FormControl>
      </SimpleGrid>

      <HStack justify="flex-end" pt={2} spacing={3}>
        <Button variant="outline" onClick={onCancelar} isDisabled={guardando}>Cancelar</Button>
        <Button bg={IMM_COLOR} color="white" _hover={{ bg: IMM_COLOR_HOVER }} onClick={handleGuardar} isLoading={guardando} loadingText="Guardando...">
          Guardar constancia
        </Button>
      </HStack>
    </VStack>
  );
};

export default FormularioConstancia;
